// Firebase imports
import {
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc,
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { toDRXError } from '@/lib/drx-error';

// Progress entity interfaces
export interface CreateLessonProgressData {
  userId: string;
  courseId: string;
  moduleId: string;
  lessonId: string;
  status: 'notStarted' | 'inProgress' | 'completed';
  completionPercentage: number;
  timeSpentSeconds: number;
  startedAt?: Date;
  completedAt?: Date;
  lastAccessedAt: Date;
  videoProgressSeconds?: number;
  totalVideoDurationSeconds?: number;
  notes?: string;
  bookmarked?: boolean;
}

export interface UpdateLessonProgressData {
  status?: 'notStarted' | 'inProgress' | 'completed';
  completionPercentage?: number;
  timeSpentSeconds?: number;
  completedAt?: Date;
  lastAccessedAt?: Date;
  videoProgressSeconds?: number;
  notes?: string;
  bookmarked?: boolean;
  updatedAt?: Timestamp;
}

export interface CreateQuizProgressData {
  userId: string;
  courseId: string;
  moduleId?: string;
  lessonId?: string;
  quizId: string;
  attemptNumber: number;
  status: 'notStarted' | 'inProgress' | 'completed' | 'failed';
  score?: number;
  maxScore: number;
  passingScore: number;
  questionsAnswered: number;
  totalQuestions: number;
  timeSpentSeconds: number;
  startedAt: Date;
  completedAt?: Date;
  answers: QuizAnswerData[];
  passed: boolean;
}

export interface QuizAnswerData {
  questionId: string;
  questionText: string;
  userAnswer: string | string[];
  correctAnswer: string | string[];
  isCorrect: boolean;
  pointsEarned: number;
  maxPoints: number;
  timeSpentSeconds: number;
}

export interface UpdateQuizProgressData {
  status?: 'notStarted' | 'inProgress' | 'completed' | 'failed';
  score?: number;
  questionsAnswered?: number;
  timeSpentSeconds?: number;
  completedAt?: Date;
  answers?: QuizAnswerData[];
  passed?: boolean;
  updatedAt?: Timestamp;
}

export interface CreateModuleProgressData {
  userId: string;
  courseId: string;
  moduleId: string;
  status: 'notStarted' | 'inProgress' | 'completed';
  completionPercentage: number;
  lessonsCompleted: number;
  totalLessons: number;
  quizzesCompleted: number;
  totalQuizzes: number;
  timeSpentSeconds: number;
  startedAt?: Date;
  completedAt?: Date;
  lastAccessedAt: Date;
}

export interface UpdateModuleProgressData {
  status?: 'notStarted' | 'inProgress' | 'completed';
  completionPercentage?: number;
  lessonsCompleted?: number;
  quizzesCompleted?: number;
  timeSpentSeconds?: number;
  completedAt?: Date;
  lastAccessedAt?: Date;
  updatedAt?: Timestamp;
}

export interface CreateCourseProgressData {
  userId: string;
  courseId: string;
  enrollmentId?: string;
  status: 'notStarted' | 'inProgress' | 'completed';
  completionPercentage: number;
  modulesCompleted: number;
  totalModules: number;
  lessonsCompleted: number;
  totalLessons: number;
  quizzesCompleted: number;
  totalQuizzes: number;
  totalTimeSpentSeconds: number;
  averageQuizScore?: number;
  startedAt?: Date;
  completedAt?: Date;
  lastAccessedAt: Date;
  certificateIssued?: boolean;
  certificateId?: string;
}

export interface UpdateCourseProgressData {
  status?: 'notStarted' | 'inProgress' | 'completed';
  completionPercentage?: number;
  modulesCompleted?: number;
  lessonsCompleted?: number;
  quizzesCompleted?: number;
  totalTimeSpentSeconds?: number;
  averageQuizScore?: number;
  completedAt?: Date;
  lastAccessedAt?: Date;
  certificateIssued?: boolean;
  certificateId?: string;
  updatedAt?: Timestamp;
}

export interface GetLessonProgressData extends CreateLessonProgressData {
  id: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface GetQuizProgressData extends CreateQuizProgressData {
  id: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface GetModuleProgressData extends CreateModuleProgressData {
  id: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface GetCourseProgressData extends CreateCourseProgressData {
  id: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface ProgressFilter {
  userId?: string;
  courseId?: string;
  moduleId?: string;
  lessonId?: string;
  quizId?: string;
  status?: string;
  completedAfter?: Date;
  completedBefore?: Date;
}

export interface IProgressRepository {
  // Lesson Progress
  createLessonProgress(progressData: CreateLessonProgressData): Promise<GetLessonProgressData>;
  updateLessonProgress(progressId: string, progressData: UpdateLessonProgressData): Promise<GetLessonProgressData>;
  getLessonProgress(progressId: string): Promise<GetLessonProgressData | null>;
  getLessonProgressByUser(userId: string, lessonId: string): Promise<GetLessonProgressData | null>;
  getUserLessonProgress(userId: string, courseId?: string): Promise<GetLessonProgressData[]>;

  // Quiz Progress
  createQuizProgress(progressData: CreateQuizProgressData): Promise<GetQuizProgressData>;
  updateQuizProgress(progressId: string, progressData: UpdateQuizProgressData): Promise<GetQuizProgressData>;
  getQuizProgress(progressId: string): Promise<GetQuizProgressData | null>;
  getQuizProgressByUser(userId: string, quizId: string): Promise<GetQuizProgressData[]>;
  getUserQuizProgress(userId: string, courseId?: string): Promise<GetQuizProgressData[]>;

  // Module Progress
  createModuleProgress(progressData: CreateModuleProgressData): Promise<GetModuleProgressData>;
  updateModuleProgress(progressId: string, progressData: UpdateModuleProgressData): Promise<GetModuleProgressData>;
  getModuleProgress(progressId: string): Promise<GetModuleProgressData | null>;
  getModuleProgressByUser(userId: string, moduleId: string): Promise<GetModuleProgressData | null>;
  getUserModuleProgress(userId: string, courseId?: string): Promise<GetModuleProgressData[]>;

  // Course Progress
  createCourseProgress(progressData: CreateCourseProgressData): Promise<GetCourseProgressData>;
  updateCourseProgress(progressId: string, progressData: UpdateCourseProgressData): Promise<GetCourseProgressData>;
  getCourseProgress(progressId: string): Promise<GetCourseProgressData | null>;
  getCourseProgressByUser(userId: string, courseId: string): Promise<GetCourseProgressData | null>;
  getUserCourseProgress(userId: string): Promise<GetCourseProgressData[]>;

  // Batch Operations
  batchUpdateProgress(updates: { 
    lessonProgress?: { id: string; data: UpdateLessonProgressData }[];
    quizProgress?: { id: string; data: UpdateQuizProgressData }[];
    moduleProgress?: { id: string; data: UpdateModuleProgressData }[];
    courseProgress?: { id: string; data: UpdateCourseProgressData }[];
  }): Promise<void>;
}

// Firestore rejects a write containing an undefined field. Optional stats
// such as averageQuizScore are undefined until the first quiz is passed, so
// completing a lesson failed client-side -- and showed the learner an error
// -- even though the server had already recorded the completion.
function definedOnly<T extends object>(data: T): Partial<T> {
  return Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined)) as Partial<T>;
}

export class ProgressRepository implements IProgressRepository {
  private lessonProgressCollection = 'lessonProgress';
  private quizProgressCollection = 'quizProgress';
  private moduleProgressCollection = 'moduleProgress';
  private courseProgressCollection = 'courseProgress';

  constructor() {
    console.log('[ProgressRepository] Constructor - Collections initialized');
  }

  private toRepositoryError(
    error: unknown,
    code: string,
    devMessage: string,
    context?: Record<string, unknown>,
  ) {
    return toDRXError(error, {
      code,
      devMessage,
      context,
    });
  }

  // ===== LESSON PROGRESS =====

  async createLessonProgress(progressData: CreateLessonProgressData): Promise<GetLessonProgressData> {
    console.log('[ProgressRepository.createLessonProgress] START - Data:', progressData);
    try {
      const docRef = await addDoc(collection(db, this.lessonProgressCollection), {
        ...definedOnly(progressData),
        startedAt: progressData.startedAt || serverTimestamp(),
        lastAccessedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const newProgress = await this.getLessonProgress(docRef.id);
      console.log('[ProgressRepository.createLessonProgress] SUCCESS - Created:', docRef.id);
      return newProgress!;
    } catch (error) {
      console.error('[ProgressRepository.createLessonProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'LESSON_PROGRESS_CREATE_FAILED', 'Failed to create lesson progress', {
        userId: progressData.userId,
        courseId: progressData.courseId,
        moduleId: progressData.moduleId,
        lessonId: progressData.lessonId,
        status: progressData.status,
        completionPercentage: progressData.completionPercentage,
      });
    }
  }

  async updateLessonProgress(progressId: string, progressData: UpdateLessonProgressData): Promise<GetLessonProgressData> {
    console.log('[ProgressRepository.updateLessonProgress] START - ID:', progressId, 'Data:', progressData);
    try {
      const docRef = doc(db, this.lessonProgressCollection, progressId);
      await updateDoc(docRef, {
        ...definedOnly(progressData),
        updatedAt: serverTimestamp(),
      });

      const updatedProgress = await this.getLessonProgress(progressId);
      console.log('[ProgressRepository.updateLessonProgress] SUCCESS - Updated:', progressId);
      return updatedProgress!;
    } catch (error) {
      console.error('[ProgressRepository.updateLessonProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'LESSON_PROGRESS_UPDATE_FAILED', 'Failed to update lesson progress', {
        progressId,
        progressData,
      });
    }
  }

  async getLessonProgress(progressId: string): Promise<GetLessonProgressData | null> {
    console.log('[ProgressRepository.getLessonProgress] START - ID:', progressId);
    try {
      const docRef = doc(db, this.lessonProgressCollection, progressId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const progress = { id: docSnap.id, ...docSnap.data() } as GetLessonProgressData;
        console.log('[ProgressRepository.getLessonProgress] SUCCESS - Found progress');
        return progress;
      } else {
        console.log('[ProgressRepository.getLessonProgress] No progress found');
        return null;
      }
    } catch (error) {
      console.error('[ProgressRepository.getLessonProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'LESSON_PROGRESS_GET_FAILED', 'Failed to get lesson progress', {
        progressId,
      });
    }
  }

  async getLessonProgressByUser(userId: string, lessonId: string): Promise<GetLessonProgressData | null> {
    console.log('[ProgressRepository.getLessonProgressByUser] START - User:', userId, 'Lesson:', lessonId);
    try {
      const q = query(
        collection(db, this.lessonProgressCollection),
        where('userId', '==', userId),
        where('lessonId', '==', lessonId),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const progress = { id: doc.id, ...doc.data() } as GetLessonProgressData;
        console.log('[ProgressRepository.getLessonProgressByUser] SUCCESS - Found progress');
        return progress;
      } else {
        console.log('[ProgressRepository.getLessonProgressByUser] No progress found');
        return null;
      }
    } catch (error) {
      console.error('[ProgressRepository.getLessonProgressByUser] ERROR:', error);
      throw this.toRepositoryError(error, 'LESSON_PROGRESS_GET_BY_USER_FAILED', 'Failed to get lesson progress by user', {
        userId,
        lessonId,
      });
    }
  }

  async getUserLessonProgress(userId: string, courseId?: string): Promise<GetLessonProgressData[]> {
    console.log('[ProgressRepository.getUserLessonProgress] START - User:', userId, 'Course:', courseId);
    try {
      // Simplified query to avoid composite index requirement
      let q = query(
        collection(db, this.lessonProgressCollection),
        where('userId', '==', userId)
      );

      if (courseId) {
        q = query(
          collection(db, this.lessonProgressCollection),
          where('userId', '==', userId),
          where('courseId', '==', courseId)
        );
      }

      const querySnapshot = await getDocs(q);
      const progress: GetLessonProgressData[] = [];

      querySnapshot.forEach((doc) => {
        progress.push({ id: doc.id, ...doc.data() } as GetLessonProgressData);
      });

      // Sort in memory instead of using orderBy to avoid index requirement
      progress.sort((a, b) => {
        const dateA = a.lastAccessedAt instanceof Date ? a.lastAccessedAt.getTime() : 
                     a.lastAccessedAt?.seconds ? a.lastAccessedAt.seconds * 1000 : 0;
        const dateB = b.lastAccessedAt instanceof Date ? b.lastAccessedAt.getTime() : 
                     b.lastAccessedAt?.seconds ? b.lastAccessedAt.seconds * 1000 : 0;
        return dateB - dateA;
      });

      console.log('[ProgressRepository.getUserLessonProgress] SUCCESS - Found:', progress.length);
      return progress;
    } catch (error) {
      console.error('[ProgressRepository.getUserLessonProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'LESSON_PROGRESS_LIST_FOR_USER_FAILED', 'Failed to get user lesson progress', {
        userId,
        courseId,
      });
    }
  }

  // ===== QUIZ PROGRESS =====

  async createQuizProgress(progressData: CreateQuizProgressData): Promise<GetQuizProgressData> {
    console.log('[ProgressRepository.createQuizProgress] START - Data:', progressData);
    try {
      const docRef = await addDoc(collection(db, this.quizProgressCollection), {
        ...definedOnly(progressData),
        startedAt: progressData.startedAt || serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const newProgress = await this.getQuizProgress(docRef.id);
      console.log('[ProgressRepository.createQuizProgress] SUCCESS - Created:', docRef.id);
      return newProgress!;
    } catch (error) {
      console.error('[ProgressRepository.createQuizProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'QUIZ_PROGRESS_CREATE_FAILED', 'Failed to create quiz progress', {
        userId: progressData.userId,
        courseId: progressData.courseId,
        moduleId: progressData.moduleId,
        lessonId: progressData.lessonId,
        quizId: progressData.quizId,
        attemptNumber: progressData.attemptNumber,
        status: progressData.status,
      });
    }
  }

  async updateQuizProgress(progressId: string, progressData: UpdateQuizProgressData): Promise<GetQuizProgressData> {
    console.log('[ProgressRepository.updateQuizProgress] START - ID:', progressId, 'Data:', progressData);
    try {
      const docRef = doc(db, this.quizProgressCollection, progressId);
      await updateDoc(docRef, {
        ...definedOnly(progressData),
        updatedAt: serverTimestamp(),
      });

      const updatedProgress = await this.getQuizProgress(progressId);
      console.log('[ProgressRepository.updateQuizProgress] SUCCESS - Updated:', progressId);
      return updatedProgress!;
    } catch (error) {
      console.error('[ProgressRepository.updateQuizProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'QUIZ_PROGRESS_UPDATE_FAILED', 'Failed to update quiz progress', {
        progressId,
        progressData,
      });
    }
  }

  async getQuizProgress(progressId: string): Promise<GetQuizProgressData | null> {
    console.log('[ProgressRepository.getQuizProgress] START - ID:', progressId);
    try {
      const docRef = doc(db, this.quizProgressCollection, progressId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const progress = { id: docSnap.id, ...docSnap.data() } as GetQuizProgressData;
        console.log('[ProgressRepository.getQuizProgress] SUCCESS - Found progress');
        return progress;
      } else {
        console.log('[ProgressRepository.getQuizProgress] No progress found');
        return null;
      }
    } catch (error) {
      console.error('[ProgressRepository.getQuizProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'QUIZ_PROGRESS_GET_FAILED', 'Failed to get quiz progress', {
        progressId,
      });
    }
  }

  async getQuizProgressByUser(userId: string, quizId: string): Promise<GetQuizProgressData[]> {
    console.log('[ProgressRepository.getQuizProgressByUser] START - User:', userId, 'Quiz:', quizId);
    try {
      const q = query(
        collection(db, this.quizProgressCollection),
        where('userId', '==', userId),
        where('quizId', '==', quizId),
        orderBy('attemptNumber', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const progress: GetQuizProgressData[] = [];

      querySnapshot.forEach((doc) => {
        progress.push({ id: doc.id, ...doc.data() } as GetQuizProgressData);
      });

      console.log('[ProgressRepository.getQuizProgressByUser] SUCCESS - Found:', progress.length, 'attempts');
      return progress;
    } catch (error) {
      console.error('[ProgressRepository.getQuizProgressByUser] ERROR:', error);
      throw this.toRepositoryError(error, 'QUIZ_PROGRESS_GET_BY_USER_FAILED', 'Failed to get quiz progress by user', {
        userId,
        quizId,
      });
    }
  }

  async getUserQuizProgress(userId: string, courseId?: string): Promise<GetQuizProgressData[]> {
    console.log('[ProgressRepository.getUserQuizProgress] START - User:', userId, 'Course:', courseId);
    try {
      // Simplified query to avoid composite index requirement
      let q = query(
        collection(db, this.quizProgressCollection),
        where('userId', '==', userId)
      );

      if (courseId) {
        q = query(
          collection(db, this.quizProgressCollection),
          where('userId', '==', userId),
          where('courseId', '==', courseId)
        );
      }

      const querySnapshot = await getDocs(q);
      const progress: GetQuizProgressData[] = [];

      querySnapshot.forEach((doc) => {
        progress.push({ id: doc.id, ...doc.data() } as GetQuizProgressData);
      });

      // Sort in memory instead of using orderBy to avoid index requirement
      progress.sort((a, b) => {
        const dateA = a.startedAt instanceof Date ? a.startedAt.getTime() : 
                     a.startedAt?.seconds ? a.startedAt.seconds * 1000 : 0;
        const dateB = b.startedAt instanceof Date ? b.startedAt.getTime() : 
                     b.startedAt?.seconds ? b.startedAt.seconds * 1000 : 0;
        return dateB - dateA;
      });

      console.log('[ProgressRepository.getUserQuizProgress] SUCCESS - Found:', progress.length);
      return progress;
    } catch (error) {
      console.error('[ProgressRepository.getUserQuizProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'QUIZ_PROGRESS_LIST_FOR_USER_FAILED', 'Failed to get user quiz progress', {
        userId,
        courseId,
      });
    }
  }

  // ===== MODULE PROGRESS =====

  async createModuleProgress(progressData: CreateModuleProgressData): Promise<GetModuleProgressData> {
    console.log('[ProgressRepository.createModuleProgress] START - Data:', progressData);
    try {
      const docRef = await addDoc(collection(db, this.moduleProgressCollection), {
        ...definedOnly(progressData),
        startedAt: progressData.startedAt || serverTimestamp(),
        lastAccessedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const newProgress = await this.getModuleProgress(docRef.id);
      console.log('[ProgressRepository.createModuleProgress] SUCCESS - Created:', docRef.id);
      return newProgress!;
    } catch (error) {
      console.error('[ProgressRepository.createModuleProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'MODULE_PROGRESS_CREATE_FAILED', 'Failed to create module progress', {
        userId: progressData.userId,
        courseId: progressData.courseId,
        moduleId: progressData.moduleId,
        status: progressData.status,
        completionPercentage: progressData.completionPercentage,
      });
    }
  }

  async updateModuleProgress(progressId: string, progressData: UpdateModuleProgressData): Promise<GetModuleProgressData> {
    console.log('[ProgressRepository.updateModuleProgress] START - ID:', progressId, 'Data:', progressData);
    try {
      const docRef = doc(db, this.moduleProgressCollection, progressId);
      await updateDoc(docRef, {
        ...definedOnly(progressData),
        updatedAt: serverTimestamp(),
      });

      const updatedProgress = await this.getModuleProgress(progressId);
      console.log('[ProgressRepository.updateModuleProgress] SUCCESS - Updated:', progressId);
      return updatedProgress!;
    } catch (error) {
      console.error('[ProgressRepository.updateModuleProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'MODULE_PROGRESS_UPDATE_FAILED', 'Failed to update module progress', {
        progressId,
        progressData,
      });
    }
  }

  async getModuleProgress(progressId: string): Promise<GetModuleProgressData | null> {
    console.log('[ProgressRepository.getModuleProgress] START - ID:', progressId);
    try {
      const docRef = doc(db, this.moduleProgressCollection, progressId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const progress = { id: docSnap.id, ...docSnap.data() } as GetModuleProgressData;
        console.log('[ProgressRepository.getModuleProgress] SUCCESS - Found progress');
        return progress;
      } else {
        console.log('[ProgressRepository.getModuleProgress] No progress found');
        return null;
      }
    } catch (error) {
      console.error('[ProgressRepository.getModuleProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'MODULE_PROGRESS_GET_FAILED', 'Failed to get module progress', {
        progressId,
      });
    }
  }

  async getModuleProgressByUser(userId: string, moduleId: string): Promise<GetModuleProgressData | null> {
    console.log('[ProgressRepository.getModuleProgressByUser] START - User:', userId, 'Module:', moduleId);
    try {
      const q = query(
        collection(db, this.moduleProgressCollection),
        where('userId', '==', userId),
        where('moduleId', '==', moduleId),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const progress = { id: doc.id, ...doc.data() } as GetModuleProgressData;
        console.log('[ProgressRepository.getModuleProgressByUser] SUCCESS - Found progress');
        return progress;
      } else {
        console.log('[ProgressRepository.getModuleProgressByUser] No progress found');
        return null;
      }
    } catch (error) {
      console.error('[ProgressRepository.getModuleProgressByUser] ERROR:', error);
      throw this.toRepositoryError(error, 'MODULE_PROGRESS_GET_BY_USER_FAILED', 'Failed to get module progress by user', {
        userId,
        moduleId,
      });
    }
  }

  async getUserModuleProgress(userId: string, courseId?: string): Promise<GetModuleProgressData[]> {
    console.log('[ProgressRepository.getUserModuleProgress] START - User:', userId, 'Course:', courseId);
    try {
      // Simplified query to avoid composite index requirement
      let q = query(
        collection(db, this.moduleProgressCollection),
        where('userId', '==', userId)
      );

      if (courseId) {
        q = query(
          collection(db, this.moduleProgressCollection),
          where('userId', '==', userId),
          where('courseId', '==', courseId)
        );
      }

      const querySnapshot = await getDocs(q);
      const progress: GetModuleProgressData[] = [];

      querySnapshot.forEach((doc) => {
        progress.push({ id: doc.id, ...doc.data() } as GetModuleProgressData);
      });

      // Sort in memory instead of using orderBy to avoid index requirement
      progress.sort((a, b) => {
        const dateA = a.lastAccessedAt instanceof Date ? a.lastAccessedAt.getTime() : 
                     a.lastAccessedAt?.seconds ? a.lastAccessedAt.seconds * 1000 : 0;
        const dateB = b.lastAccessedAt instanceof Date ? b.lastAccessedAt.getTime() : 
                     b.lastAccessedAt?.seconds ? b.lastAccessedAt.seconds * 1000 : 0;
        return dateB - dateA;
      });

      console.log('[ProgressRepository.getUserModuleProgress] SUCCESS - Found:', progress.length);
      return progress;
    } catch (error) {
      console.error('[ProgressRepository.getUserModuleProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'MODULE_PROGRESS_LIST_FOR_USER_FAILED', 'Failed to get user module progress', {
        userId,
        courseId,
      });
    }
  }

  // ===== COURSE PROGRESS =====

  async createCourseProgress(progressData: CreateCourseProgressData): Promise<GetCourseProgressData> {
    console.log('[ProgressRepository.createCourseProgress] START - Data:', progressData);
    try {
      const docRef = await addDoc(collection(db, this.courseProgressCollection), {
        ...definedOnly(progressData),
        startedAt: progressData.startedAt || serverTimestamp(),
        lastAccessedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const newProgress = await this.getCourseProgress(docRef.id);
      console.log('[ProgressRepository.createCourseProgress] SUCCESS - Created:', docRef.id);
      return newProgress!;
    } catch (error) {
      console.error('[ProgressRepository.createCourseProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_PROGRESS_CREATE_FAILED', 'Failed to create course progress', {
        userId: progressData.userId,
        courseId: progressData.courseId,
        enrollmentId: progressData.enrollmentId,
        status: progressData.status,
        completionPercentage: progressData.completionPercentage,
      });
    }
  }

  async updateCourseProgress(progressId: string, progressData: UpdateCourseProgressData): Promise<GetCourseProgressData> {
    console.log('[ProgressRepository.updateCourseProgress] START - ID:', progressId, 'Data:', progressData);
    try {
      const docRef = doc(db, this.courseProgressCollection, progressId);
      await updateDoc(docRef, {
        ...definedOnly(progressData),
        updatedAt: serverTimestamp(),
      });

      const updatedProgress = await this.getCourseProgress(progressId);
      console.log('[ProgressRepository.updateCourseProgress] SUCCESS - Updated:', progressId);
      return updatedProgress!;
    } catch (error) {
      console.error('[ProgressRepository.updateCourseProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_PROGRESS_UPDATE_FAILED', 'Failed to update course progress', {
        progressId,
        progressData,
      });
    }
  }

  async getCourseProgress(progressId: string): Promise<GetCourseProgressData | null> {
    console.log('[ProgressRepository.getCourseProgress] START - ID:', progressId);
    try {
      const docRef = doc(db, this.courseProgressCollection, progressId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const progress = { id: docSnap.id, ...docSnap.data() } as GetCourseProgressData;
        console.log('[ProgressRepository.getCourseProgress] SUCCESS - Found progress');
        return progress;
      } else {
        console.log('[ProgressRepository.getCourseProgress] No progress found');
        return null;
      }
    } catch (error) {
      console.error('[ProgressRepository.getCourseProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_PROGRESS_GET_FAILED', 'Failed to get course progress', {
        progressId,
      });
    }
  }

  async getCourseProgressByUser(userId: string, courseId: string): Promise<GetCourseProgressData | null> {
    console.log('[ProgressRepository.getCourseProgressByUser] START - User:', userId, 'Course:', courseId);
    try {
      const q = query(
        collection(db, this.courseProgressCollection),
        where('userId', '==', userId),
        where('courseId', '==', courseId),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const progress = { id: doc.id, ...doc.data() } as GetCourseProgressData;
        console.log('[ProgressRepository.getCourseProgressByUser] SUCCESS - Found progress');
        return progress;
      } else {
        console.log('[ProgressRepository.getCourseProgressByUser] No progress found');
        return null;
      }
    } catch (error) {
      console.error('[ProgressRepository.getCourseProgressByUser] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_PROGRESS_GET_BY_USER_FAILED', 'Failed to get course progress by user', {
        userId,
        courseId,
      });
    }
  }

  async getUserCourseProgress(userId: string): Promise<GetCourseProgressData[]> {
    console.log('[ProgressRepository.getUserCourseProgress] START - User:', userId);
    try {
      // Simplified query to avoid composite index requirement
      const q = query(
        collection(db, this.courseProgressCollection),
        where('userId', '==', userId)
      );

      const querySnapshot = await getDocs(q);
      const progress: GetCourseProgressData[] = [];

      querySnapshot.forEach((doc) => {
        progress.push({ id: doc.id, ...doc.data() } as GetCourseProgressData);
      });

      // Sort in memory instead of using orderBy to avoid index requirement
      progress.sort((a, b) => {
        const dateA = a.lastAccessedAt instanceof Date ? a.lastAccessedAt.getTime() : 
                     a.lastAccessedAt?.seconds ? a.lastAccessedAt.seconds * 1000 : 0;
        const dateB = b.lastAccessedAt instanceof Date ? b.lastAccessedAt.getTime() : 
                     b.lastAccessedAt?.seconds ? b.lastAccessedAt.seconds * 1000 : 0;
        return dateB - dateA;
      });

      console.log('[ProgressRepository.getUserCourseProgress] SUCCESS - Found:', progress.length);
      return progress;
    } catch (error) {
      console.error('[ProgressRepository.getUserCourseProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_PROGRESS_LIST_FOR_USER_FAILED', 'Failed to get user course progress', {
        userId,
      });
    }
  }

  // ===== BATCH OPERATIONS =====

  async batchUpdateProgress(updates: { 
    lessonProgress?: { id: string; data: UpdateLessonProgressData }[];
    quizProgress?: { id: string; data: UpdateQuizProgressData }[];
    moduleProgress?: { id: string; data: UpdateModuleProgressData }[];
    courseProgress?: { id: string; data: UpdateCourseProgressData }[];
  }): Promise<void> {
    console.log('[ProgressRepository.batchUpdateProgress] START - Updates:', updates);
    try {
      const batch = writeBatch(db);

      // Update lesson progress
      if (updates.lessonProgress) {
        for (const update of updates.lessonProgress) {
          const docRef = doc(db, this.lessonProgressCollection, update.id);
          batch.update(docRef, {
            ...update.data,
            updatedAt: serverTimestamp(),
          });
        }
      }

      // Update quiz progress
      if (updates.quizProgress) {
        for (const update of updates.quizProgress) {
          const docRef = doc(db, this.quizProgressCollection, update.id);
          batch.update(docRef, {
            ...update.data,
            updatedAt: serverTimestamp(),
          });
        }
      }

      // Update module progress
      if (updates.moduleProgress) {
        for (const update of updates.moduleProgress) {
          const docRef = doc(db, this.moduleProgressCollection, update.id);
          batch.update(docRef, {
            ...update.data,
            updatedAt: serverTimestamp(),
          });
        }
      }

      // Update course progress
      if (updates.courseProgress) {
        for (const update of updates.courseProgress) {
          const docRef = doc(db, this.courseProgressCollection, update.id);
          batch.update(docRef, {
            ...update.data,
            updatedAt: serverTimestamp(),
          });
        }
      }

      await batch.commit();
      console.log('[ProgressRepository.batchUpdateProgress] SUCCESS - Batch updates completed');
    } catch (error) {
      console.error('[ProgressRepository.batchUpdateProgress] ERROR:', error);
      throw this.toRepositoryError(error, 'PROGRESS_BATCH_UPDATE_FAILED', 'Failed to batch update progress', {
        lessonProgressCount: updates.lessonProgress?.length ?? 0,
        quizProgressCount: updates.quizProgress?.length ?? 0,
        moduleProgressCount: updates.moduleProgress?.length ?? 0,
        courseProgressCount: updates.courseProgress?.length ?? 0,
      });
    }
  }
}
